import { useCallback, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { toastVariant, useToast } from 'components/ToastProvider';
import PerformActionService from 'services/PerformActionService';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import { useTranslation } from 'react-i18next';

export const BULK_ACTION_TYPES = {
  page: 'page',
  search: 'search',
  group: 'group',
  all: 'all',
  individual: 'individual',
  none: 'none',
};

export const SEND_EMAIL_IDENTIFIER = 'send_email';
export const SEND_TEXT_IDENTIFIER = 'send_text';
export const SEND_SURVEY_IDENTIFIER = 'send_survey';
export const CHANGE_TAGS_IDENTIFIER = 'change_tags';
export const MODIFY_AUTOMATION_IDENTIFIER = 'start_automation';
export const MANAGE_TEAM_IDENTIFIER = 'manage_team';
export const MANAGE_SUBSCRIPTIONS = 'manage_subscription';
export const EXPORT_IDENTIFIER = 'export';
export const ARCHIVE_IDENTIFIER = 'archive';
export const CHANGE_FIELD_IDENTIFIER = 'change_field_value';
export const CUSTOM_ACTION_IDENTIFIER = 'custom_action';

export const BULK_ACTION_TO_BULK_PERMISSION = {
  [SEND_EMAIL_IDENTIFIER]: 'bulk_send_email',
  [SEND_TEXT_IDENTIFIER]: 'bulk_send_text',
  [SEND_SURVEY_IDENTIFIER]: 'bulk_send_survey',
  [CHANGE_TAGS_IDENTIFIER]: 'bulk_change_tags',
  [MODIFY_AUTOMATION_IDENTIFIER]: 'bulk_start_automation',
  [MANAGE_TEAM_IDENTIFIER]: 'bulk_manage_associations',
  [MANAGE_SUBSCRIPTIONS]: 'bulk_manage_subscriptions',
  [EXPORT_IDENTIFIER]: 'bulk_export_to_csv',
  [ARCHIVE_IDENTIFIER]: 'bulk_archive',
  [CHANGE_FIELD_IDENTIFIER]: 'bulk_change_field_value',
};

const defaultSteps = (t) => [
  { title: t('Choose Action') },
  { title: t('Send Email') },
  { title: t('Confirm') },
];

export const getCustomActionOptions = (customActions, t) =>
  customActions.map((customAction) => ({
    value: CUSTOM_ACTION_IDENTIFIER,
    label: customAction.name,
    steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
    titleForModal: t('{{name}} on', customAction),
    customAction,
  }));

export const getPerformActionOptions = (t, entityNamePlural = 'Contacts') => [
  {
    value: SEND_EMAIL_IDENTIFIER,
    label: t('Send Email'),
    steps: defaultSteps(t),
    titleForModal: t('Send Email to'),
  },
  // temporary disabled 'Send Text' action as to https://kizen.atlassian.net/browse/KZN-8261
  // {
  //   value: SEND_TEXT_IDENTIFIER,
  //   label: t('Send Text'),
  //   steps: [
  //     { title: t('Choose Action') },
  //     { title: t('Send Text') },
  //     { title: t('Confirm') },
  //   ],
  //   titleForModal: t('Send Text to'),
  //   isPrereleaseFeature: true,
  // },
  {
    value: CHANGE_TAGS_IDENTIFIER,
    label: t('Change Tags'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Change Tags') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Change Tags on'),
  },
  {
    value: CHANGE_FIELD_IDENTIFIER,
    label: t('Change Field Value'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Change Field Value') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Change Field Value on'),
  },
  {
    value: MODIFY_AUTOMATION_IDENTIFIER,
    label: t('Modify Automation'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Modify Automation') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Modify Automation on'),
  },
  {
    value: MANAGE_TEAM_IDENTIFIER,
    label: t('Manage Team'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Manage Team') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Manage Team on'),
  },
  {
    value: MANAGE_SUBSCRIPTIONS,
    label: t('Manage Subscriptions'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Manage Subscriptions') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Manage Subscription Lists on'),
  },
  {
    value: EXPORT_IDENTIFIER,
    label: t('Export to CSV'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Export Options') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Export'),
  },
  {
    value: ARCHIVE_IDENTIFIER,
    label: `${t('Archive')} ${entityNamePlural}`,
    steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
    titleForModal: t('Archive'),
  },
];

const getAction = (
  selectedActionValue,
  performActionOptions,
  secondaryAction
) => {
  if (selectedActionValue === MODIFY_AUTOMATION_IDENTIFIER) {
    return `${secondaryAction || 'start'}_automation`;
  }

  // we really want the selected action
  if (selectedActionValue) {
    return selectedActionValue;
  }

  // we now look at the available options
  if (performActionOptions?.length) {
    // we would prefer not to use email or text as they may a have a lesser count
    const preferedActions = performActionOptions.filter(
      ({ value }) => !['bulk_send_email', 'bulk_send_text'].includes(value)
    );

    return preferedActions?.length
      ? preferedActions[0].value
      : performActionOptions[0].value;
  }

  // a default
  return 'change_field_value';
};
const POLLING_TIMEOUTS = [1000, 3000, 5000, 7000, 9000];

// BE filter priority is: group > filters
export const useGetSummary = ({
  objectId,
  selection,
  records,
  groupOptions,
  groupId,
  search,
  filters,
  field,
  fieldValue,
  selectedAction,
  performActionOptions,
  subscriptionLists,
  subscriptionListOptions,
  status,
  subscriptionItem,
  secondaryAction,
  automation,
  customAction,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [isPolling, setIsPolling] = useState(false);
  const [startSummaryResult, setStartSummaryResult] = useState(null);
  const [summary, setSummary] = useState(null);

  const [{ loading: isFetching }, startGetSummary] = useAsyncFn(async () => {
    try {
      const commonBody = {
        custom_object_id: objectId,
        subtract_record_ids: selection.subtractIds,
        field_id: field?.id,
        field_value: fieldValue?.code,
        automation_id: automation?.value || customAction?.automation?.id,
        action: getAction(
          selectedAction?.value,
          performActionOptions,
          secondaryAction
        ),
      };

      if (selectedAction?.value === CUSTOM_ACTION_IDENTIFIER) {
        commonBody.custom_action_id = customAction?.id;
        commonBody.action = customAction?.action;
      }

      // Handle params for subscription list selection
      if (
        selectedAction?.value === SEND_EMAIL_IDENTIFIER &&
        subscriptionLists
      ) {
        if (subscriptionLists.length) {
          commonBody.subscription_list_ids = subscriptionLists;
        } else {
          // An empty selection means "all subscription lists"
          commonBody.subscription_list_ids = subscriptionListOptions.map(
            (opt) => opt.value
          );
        }
      }
      // special summary request for MANAGE_SUBSCRIPTIONS
      if (
        selectedAction?.value === MANAGE_SUBSCRIPTIONS &&
        status &&
        subscriptionItem
      ) {
        commonBody.subscription_list_ids = [subscriptionItem.value];
        commonBody.subscription_list_status = status;
      }

      if (selection.value === 'page' && records) {
        commonBody.record_ids = selection.checkedIds;
      } else if (selection.value === 'group') {
        const group = groupOptions.find((el) => el.id === groupId);
        commonBody.in_group_ids = group?.id && [group?.id];
      } else if (selection.value === 'search' && selection.checkedCount) {
        commonBody.search_text = search || undefined;
        commonBody.filters = filters;
      } else if (
        selection.value === 'individual' &&
        selection.checkedIds.length !== 0
      ) {
        commonBody.record_ids = selection.checkedIds;
      }

      const response = await PerformActionService.startSummary(commonBody);
      const result =
        selectedAction?.value === CUSTOM_ACTION_IDENTIFIER
          ? {
              ...response,
              action: response?.action || CUSTOM_ACTION_IDENTIFIER,
            }
          : response;
      setIsPolling(true);
      setStartSummaryResult(result);
      return result;
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('An error occurred. Please try again or contact support.'),
        variant: toastVariant.FAILURE,
      });
      setIsPolling(false);
      setStartSummaryResult(null);
      throw error;
    }
  }, [
    objectId,
    selection,
    records,
    groupOptions,
    groupId,
    search,
    filters,
    field?.id,
    fieldValue?.code,
    automation?.value,
    selectedAction?.value,
    performActionOptions,
    subscriptionLists,
    subscriptionListOptions,
    status,
    subscriptionItem,
    secondaryAction,
    customAction?.id,
  ]);

  const getSummaryFn = useCallback(async () => {
    if (
      startSummaryResult?.action &&
      startSummaryResult.action === selectedAction?.value &&
      startSummaryResult?.custom_action?.id &&
      startSummaryResult.custom_action.id === customAction?.id
    ) {
      return;
    } else {
      await startGetSummary();
    }
  }, [
    startGetSummary,
    startSummaryResult?.action,
    startSummaryResult?.custom_action?.id,
    selectedAction?.value,
    customAction?.id,
  ]);

  useEffect(() => {
    setStartSummaryResult(null);
  }, [startGetSummary]);

  useEffect(() => {
    let timeout;
    let reset = false;
    setSummary(null);

    if (startSummaryResult?.summary_id) {
      setIsPolling(true);

      let count = 0;

      const poll = async () => {
        try {
          const summaryResult = await PerformActionService.getSummary(
            startSummaryResult.summary_id
          );

          if (reset) {
            return;
          }

          switch (summaryResult.status) {
            case 'completed':
              clearTimeout(timeout);
              setSummary(summaryResult);
              setIsPolling(false);
              break;
            case 'failed':
              clearTimeout(timeout);
              setSummary(null);
              setIsPolling(false);
              showToast({
                message:
                  getErrorMessage(summaryResult.errors) ||
                  t('An error occurred. Please try again or contact support.'),
                variant: toastVariant.FAILURE,
              });
              break;
            default:
              timeout = setTimeout(poll, POLLING_TIMEOUTS[count++] || 10_000);
          }
        } catch (error) {
          clearTimeout(timeout);
          setIsPolling(false);
          setSummary(null);
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('An error occurred. Please try again or contact support.'),
            variant: toastVariant.FAILURE,
          });
        }
      };

      poll();
    } else {
      setIsPolling(false);
    }

    return () => {
      clearTimeout(timeout);
      setIsPolling(false);
      reset = true;
    };
  }, [startSummaryResult?.summary_id, showToast, t]);

  return [{ summary, isFetching, isPolling }, getSummaryFn];
};

export const moneyFormatter = (string) =>
  Intl.NumberFormat('en', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(string);

export const numberFormatter = (string) =>
  Intl.NumberFormat('en').format(string);
